import { useEffect, useState } from "react";
import { Clock, Timer, Shuffle, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";

const DAYS = [
  { v: 0, label: "Mon" }, { v: 1, label: "Tue" }, { v: 2, label: "Wed" },
  { v: 3, label: "Thu" }, { v: 4, label: "Fri" }, { v: 5, label: "Sat" }, { v: 6, label: "Sun" },
];

export default function OperationsSettings() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/settings/operations")
      .then(({ data }) => setCfg(data.data))
      .catch((e) => setError(apiError(e)));
  }, []);

  const set = (patch) => setCfg((c) => ({ ...c, ...patch }));
  const toggleDay = (v) => set({
    business_days: cfg.business_days.includes(v)
      ? cfg.business_days.filter((d) => d !== v)
      : [...cfg.business_days, v].sort((a, b) => a - b),
  });

  const save = async () => {
    setSaving(true); setError("");
    try {
      const { data } = await api.put("/settings/operations", {
        business_hours_start: cfg.business_hours_start,
        business_hours_end: cfg.business_hours_end,
        business_days: cfg.business_days,
        sla_target_minutes: Number(cfg.sla_target_minutes),
        sla_auto_reshuffle: cfg.sla_auto_reshuffle,
      });
      setCfg(data.data);
      toast.success("Operations settings saved");
    } catch (e) { setError(apiError(e)); toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  if (!cfg) return (
    <div className="rounded-2xl border border-sky/70 bg-white p-6" data-testid="operations-settings">
      <p className="text-sm text-ink/50">{error || "Loading operations settings…"}</p>
    </div>
  );

  const hrs = Math.floor(cfg.sla_target_minutes / 60);
  const mins = cfg.sla_target_minutes % 60;

  return (
    <div className="rounded-2xl border border-sky/70 bg-white p-6" data-testid="operations-settings">
      <h2 className="mb-1 flex items-center gap-2 font-sora font-semibold text-navy">
        <Clock className="h-5 w-5 text-loop" />Operations &amp; SLA
      </h2>
      <p className="mb-5 text-sm text-ink/60">Office hours drive lead auto-assignment and the response-SLA clock (counts business hours only).</p>

      {/* Office hours */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-ink/70">Office opens (IST)</span>
          <input type="time" data-testid="ops-start" value={cfg.business_hours_start}
            onChange={(e) => set({ business_hours_start: e.target.value })}
            className="h-10 w-full rounded-lg border border-sky px-3 text-sm outline-none focus:border-loop" />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-ink/70">Office closes (IST)</span>
          <input type="time" data-testid="ops-end" value={cfg.business_hours_end}
            onChange={(e) => set({ business_hours_end: e.target.value })}
            className="h-10 w-full rounded-lg border border-sky px-3 text-sm outline-none focus:border-loop" />
        </label>
      </div>

      {/* Working days */}
      <div className="mt-4">
        <span className="mb-2 block text-xs font-medium text-ink/70">Working days</span>
        <div className="flex flex-wrap gap-2">
          {DAYS.map((d) => {
            const on = cfg.business_days.includes(d.v);
            return (
              <button key={d.v} type="button" data-testid={`ops-day-${d.v}`} onClick={() => toggleDay(d.v)}
                className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors ${on ? "border-loop bg-loop text-white" : "border-sky bg-white text-ink/60 hover:border-loop"}`}>
                {d.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* SLA target */}
      <div className="mt-5 border-t border-sky/50 pt-5">
        <label className="block max-w-xs">
          <span className="mb-1 flex items-center gap-1.5 text-xs font-medium text-ink/70"><Timer className="h-3.5 w-3.5 text-loop" />SLA response target (minutes)</span>
          <input type="number" min="5" data-testid="ops-sla-target" value={cfg.sla_target_minutes}
            onChange={(e) => set({ sla_target_minutes: e.target.value })}
            className="h-10 w-full rounded-lg border border-sky px-3 text-sm outline-none focus:border-loop" />
          <span className="mt-1 block text-[11px] text-ink/40">= {hrs > 0 ? `${hrs}h ` : ""}{mins > 0 ? `${mins}m` : (hrs > 0 ? "" : "0m")} of business time to first contact.</span>
        </label>
      </div>

      {/* Auto-reshuffle toggle */}
      <div className="mt-5 flex items-start justify-between gap-4 rounded-xl border border-sky/60 bg-mist/40 p-4">
        <div className="flex items-start gap-2.5">
          <Shuffle className="mt-0.5 h-4 w-4 text-loop" />
          <div>
            <p className="text-sm font-semibold text-navy">Auto-reshuffle on SLA breach</p>
            <p className="text-xs text-ink/55">If a salesperson doesn't contact a lead within the SLA target, automatically transfer it to another available salesperson.</p>
          </div>
        </div>
        <button type="button" role="switch" aria-checked={cfg.sla_auto_reshuffle} data-testid="ops-reshuffle-toggle"
          onClick={() => set({ sla_auto_reshuffle: !cfg.sla_auto_reshuffle })}
          className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full px-0.5 transition-colors duration-200 ${cfg.sla_auto_reshuffle ? "bg-loop" : "bg-slate-300"}`}>
          <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform duration-200 ${cfg.sla_auto_reshuffle ? "translate-x-5" : "translate-x-0"}`} />
        </button>
      </div>

      {error && <p className="mt-3 text-sm text-red-500">{error}</p>}
      <button onClick={save} disabled={saving} data-testid="ops-save"
        className="mt-5 flex items-center gap-2 rounded-xl bg-loop px-4 py-2.5 text-sm font-semibold text-white hover:bg-loop/90 disabled:opacity-60">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}Save changes
      </button>
    </div>
  );
}
